import Phaser from 'phaser';
import { IconLoader } from './IconLoader';
import { getResourceCapacityForStage } from '../config/ResourceCapacityConfig';

export class DOMUIManager {
  private scene: Phaser.Scene;
  private elements: Map<string, Phaser.GameObjects.DOMElement> = new Map();
  private styleLink: HTMLLinkElement | null = null;
  // Track HTML elements created directly in the DOM
  private domElements: Map<string, HTMLElement> = new Map();
  private _callbacks: Map<string, (type: string) => void> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.initStylesheet();
    
    // Clean up DOM elements when scene is shutdown
    this.scene.events.on('shutdown', this.destroy, this);
  }

  /**
   * Initialize the stylesheet for UI elements
   */
  private initStylesheet(): void {
    // Check if stylesheet link already exists
    if (document.getElementById('cytopod-ui-styles-link')) {
      return;
    }

    // Determine the correct path to CSS based on environment
    let cssPath = './assets/cytopod-ui.css'; // Default/production path
    
    // In development mode, use the direct path to the source file
    // Check if we're in development by looking at the URL
    const isDevelopment = window.location.hostname === 'localhost' || 
                        window.location.hostname === '127.0.0.1';
    
    if (isDevelopment) {
      console.log('Development environment detected, using source CSS path');
      cssPath = '/src/ui/cytopod-ui.css';
    } else {
      // Add cache-busting parameter for production
      // This uses a timestamp to ensure the browser fetches the latest version
      const cacheBuster = `v=${Date.now()}`;
      cssPath = `./assets/cytopod-ui.css?${cacheBuster}`;
      console.log(`Adding cache buster to CSS: ${cacheBuster}`);
    }

    console.log(`Loading CSS from: ${cssPath}`);

    // Create link to external stylesheet
    this.styleLink = document.createElement('link');
    this.styleLink.id = 'cytopod-ui-styles-link';
    this.styleLink.rel = 'stylesheet';
    this.styleLink.type = 'text/css';
    this.styleLink.href = cssPath;
    document.head.appendChild(this.styleLink);
  }

  /**
   * Create a DOM element
   * @param id Unique ID for the element
   * @param html HTML content
   * @param x X position 
   * @param y Y position
   * @param width Width of the element
   * @param height Height of the element
   * @returns The created DOM element
   */
  public createElement(id: string, html: string, x: number, y: number, width?: number, height?: number): Phaser.GameObjects.DOMElement {
    // Remove existing element with same ID if it exists
    if (this.elements.has(id)) {
      this.elements.get(id)?.destroy();
    }

    // Create the DOM element
    const element = this.scene.add.dom(x, y).createFromHTML(html);
    
    // Set width and height if provided
    if (width) {
      // Using any type to avoid linter errors since Phaser types might be incomplete
      (element as any).setDisplaySize(width, height || width);
    }
    
    // Store reference
    this.elements.set(id, element);
    
    return element;
  }

  /**
   * Create a DOM element directly in the document body (not in the Phaser canvas)
   * This allows for proper fixed positioning relative to the viewport
   */
  public createDOMElement(id: string, html: string): HTMLElement {
    // Remove existing element if it exists
    if (this.domElements.has(id)) {
      const existingElement = this.domElements.get(id);
      if (existingElement && existingElement.parentNode) {
        existingElement.parentNode.removeChild(existingElement);
      }
      this.domElements.delete(id);
    }

    // Find the game container - this is where we want to append our elements
    const gameContainer = document.getElementById('game-container');
    
    // Create a container for the HTML content
    const container = document.createElement('div');
    container.id = id;
    container.innerHTML = html;
    
    // Append to game container or body if game container not found
    if (gameContainer) {
      gameContainer.appendChild(container);
    } else {
      document.body.appendChild(container);
    }
    
    // Store reference
    this.domElements.set(id, container);
    
    return container;
  }

  /**
   * Create a container for UI elements in the top right
   */
  public createTopRightContainer(): HTMLElement {
    // Create HTML for the container
    const containerHTML = `
      <div id="top-right-container-inner" style="position: fixed; top: 0; right: 0; padding: 10px; display: flex; flex-direction: column; align-items: flex-end; z-index: 1000;">
      </div>
    `;

    // Create element directly in the DOM
    return this.createDOMElement('top-right-container', containerHTML);
  }

  /**
   * Create a container for UI elements in the top left
   */
  public createTopLeftContainer(): HTMLElement {
    // Create HTML for the container
    const containerHTML = `
      <div id="top-left-container-inner" style="position: fixed; top: 0; left: 0; padding: 10px; display: flex; flex-direction: column; align-items: flex-start; z-index: 1000;">
      </div>
    `;

    // Create element directly in the DOM
    return this.createDOMElement('top-left-container', containerHTML);
  }

  /**
   * Create a container for UI elements in the bottom left
   */
  public createBottomLeftContainer(): HTMLElement {
    // Create HTML for the container
    const containerHTML = `
      <div id="bottom-left-container-inner" style="position: fixed; bottom: 20px; left: 20px; display: flex; flex-direction: column; gap: 10px; z-index: 1000;">
      </div>
    `;

    // Create element directly in the DOM
    return this.createDOMElement('bottom-left-container', containerHTML);
  }

  /**
   * Create navigation buttons (back and fullscreen) in the bottom left
   */
  public createNavigationButtons(onBackClick: () => void): HTMLElement {
    // Ensure bottom-left container exists
    let container = this.domElements.get('bottom-left-container');
    if (!container) {
      container = this.createBottomLeftContainer();
    }
    
    // Get the fullscreen icon based on current state
    const fullscreenIconName = this.scene.scale.isFullscreen ? 'minimize' : 'fullscreen';
    const fullscreenIconSvg = IconLoader.getIcon(fullscreenIconName);
    const backIconSvg = IconLoader.getIcon('arrow-left');
    
    // Create HTML for the navigation buttons
    const buttonsHTML = `
      <div class="navigation-buttons">
        <button id="fullscreen-button" class="ui-button icon-button" style="width: auto; padding: 8px 15px; margin-bottom: 10px; pointer-events: auto;">
          <span class="button-icon">${fullscreenIconSvg}</span>
        </button>
        <button id="back-button" class="ui-button icon-button" style="width: auto; padding: 8px 15px; pointer-events: auto;">
          <span class="button-icon">${backIconSvg}</span>
        </button>
      </div>
    `;
    
    // Create the element directly in the DOM
    const buttonsElement = this.createDOMElement('navigation-buttons', buttonsHTML);
    
    // Move it into the bottom-left container
    const containerInner = container.querySelector('#bottom-left-container-inner');
    if (containerInner) {
      containerInner.appendChild(buttonsElement);
    }
    
    // Add event listeners
    const fullscreenButton = buttonsElement.querySelector('#fullscreen-button');
    if (fullscreenButton) {
      fullscreenButton.addEventListener('click', (event) => {
        event.stopPropagation(); // Prevent click from propagating to game
        this.toggleFullscreen();
        
        // Update the icon based on new fullscreen state
        this.updateFullscreenButtonIcon();
      });
    }
    
    const backButton = buttonsElement.querySelector('#back-button');
    if (backButton) {
      backButton.addEventListener('click', (event) => {
        event.stopPropagation(); // Prevent click from propagating to game
        onBackClick();
      });
    }
    
    return buttonsElement;
  }
  
  /**
   * Update fullscreen button icon based on current fullscreen state
   */
  private updateFullscreenButtonIcon(): void {
    const buttonsElement = this.domElements.get('navigation-buttons');
    if (!buttonsElement) return;
    
    const fullscreenButton = buttonsElement.querySelector('#fullscreen-button');
    if (!fullscreenButton) return;
    
    const iconSpan = fullscreenButton.querySelector('.button-icon');
    if (!iconSpan) return;
    
    // Get the icon based on fullscreen state
    const iconName = this.scene.scale.isFullscreen ? 'minimize' : 'fullscreen';
    iconSpan.innerHTML = IconLoader.getIcon(iconName);
  }

  /**
   * Create an organelle building menu directly in the DOM
   */
  public createOrganelleMenu(
    items: Array<{type: string, name: string, lipidCost: number, glucoseCost: number, atpCost?: number}>,
    resources: {lipids: number, glucose: number, atp?: number},
    onSelect: (type: string) => void,
    membraneInfo?: {
      currentStage: number,
      nextStage?: {
        stage: number,
        lipidCost: number,
        atpCost: number,
        requirements: { id: string, description: string }[]
      }
    }
  ): HTMLElement {
    // Store the callback for future use
    this._callbacks.set('organelle-menu-select', onSelect);
    
    // Ensure top-right container exists
    let container = this.domElements.get('top-right-container');
    if (!container) {
      container = this.createTopRightContainer();
    }
    
    // Check if we should initialize in collapsed state on mobile (width <= 768px)
    const isMobile = window.innerWidth <= 768;
    const collapsedClass = isMobile ? 'collapsed' : '';

    // Create HTML for the menu
    const menuHTML = `
      <div class="ui-container ui-panel organelle-menu" style="margin-bottom: 10px; pointer-events: auto;">
        <div class="ui-title collapsible">
          <span>Build Organelles</span>
          <span class="collapse-indicator ${collapsedClass}"></span>
        </div>
        <div class="organelle-buttons collapsible-content ${collapsedClass}">
          ${items.map(item => {
            // Determine if enough resources are available
            const canAfford = resources.lipids >= item.lipidCost && 
                             resources.glucose >= item.glucoseCost && 
                             (item.atpCost === undefined || (resources.atp || 0) >= item.atpCost);
            
            // Apply disabled class if can't afford
            const disabledClass = canAfford ? '' : 'ui-button-disabled';
            
            // Include ATP in cost display if needed
            const atpCost = item.atpCost !== undefined ? `<span class="atp-cost">${item.atpCost}</span>A` : '';
            const atpSeparator = atpCost ? '&nbsp;' : '';
            
            return `
              <button class="ui-button ${disabledClass}" data-type="${item.type}" 
                data-lipid-cost="${item.lipidCost}" data-glucose-cost="${item.glucoseCost}" ${item.atpCost !== undefined ? `data-atp-cost="${item.atpCost}"` : ''}>
                <span class="organelle-name">${item.name}</span>
                <span class="cost">
                  <span class="lipid-cost">${item.lipidCost}</span>L&nbsp;
                  <span class="glucose-cost">${item.glucoseCost}</span>G${atpSeparator}${atpCost}
                </span>
              </button>
            `;
          }).join('')}
          
          ${membraneInfo && membraneInfo.nextStage ? `
            <hr style="border: 0; border-top: 1px solid rgba(255,255,255,0.1); margin: 10px 0;">
            <button class="ui-button ${resources.lipids >= membraneInfo.nextStage.lipidCost && 
                                      (resources.atp || 0) >= membraneInfo.nextStage.atpCost ? '' : 'ui-button-disabled'}" 
                    data-type="expand_membrane" 
                    data-lipid-cost="${membraneInfo.nextStage.lipidCost}" 
                    data-atp-cost="${membraneInfo.nextStage.atpCost}">
              <span class="organelle-name">Expand Membrane to Stage ${membraneInfo.nextStage.stage}</span>
              <span class="cost">
                <span class="lipid-cost">${membraneInfo.nextStage.lipidCost}</span>L
                ${membraneInfo.nextStage.atpCost > 0 ? `&nbsp;<span class="atp-cost">${membraneInfo.nextStage.atpCost}</span>A` : ''}
              </span>
              <div class="requirements" style="font-size: 0.8em; margin-top: 5px; color: #aaa;">
                ${membraneInfo.nextStage.requirements.map(req => `
                  <div>${req.description}</div>
                `).join('')}
              </div>
            </button>
          ` : membraneInfo ? `
            <hr style="border: 0; border-top: 1px solid rgba(255,255,255,0.1); margin: 10px 0;">
            <div style="padding: 5px; color: #aaa; text-align: center;">
              Maximum membrane stage reached (${membraneInfo.currentStage})
            </div>
          ` : ''}
        </div>
      </div>
    `;

    // Create the element directly in the DOM
    const menuElement = this.createDOMElement('organelle-menu', menuHTML);
    
    // Move it into the top-right container
    const containerInner = container.querySelector('#top-right-container-inner');
    if (containerInner) {
      containerInner.appendChild(menuElement);
    }
    
    // Add event listeners for buttons
    const buttons = menuElement.querySelectorAll('.ui-button');
    buttons.forEach(button => {
      button.addEventListener('click', (event) => {
        event.stopPropagation(); // Prevent click from propagating to game
        
        // Only allow clicking if button is not disabled
        if (!button.classList.contains('ui-button-disabled')) {
          const type = button.getAttribute('data-type');
          if (type) onSelect(type);
        }
      });
    });
    
    // Add collapsible functionality
    const titleElement = menuElement.querySelector('.ui-title.collapsible');
    const contentElement = menuElement.querySelector('.collapsible-content');
    const indicatorElement = menuElement.querySelector('.collapse-indicator');
    
    if (titleElement && contentElement && indicatorElement) {
      titleElement.addEventListener('click', (event) => {
        event.stopPropagation();
        
        // Toggle collapsed state
        contentElement.classList.toggle('collapsed');
        indicatorElement.classList.toggle('collapsed');
        
        // Store the collapsed state in localStorage to persist between sessions
        const isCollapsed = contentElement.classList.contains('collapsed');
        try {
          localStorage.setItem('organelleMenuCollapsed', isCollapsed.toString());
        } catch (e) {
          // Ignore localStorage errors
        }
      });
      
      // Check if we should restore the collapsed state from previous session
      if (!isMobile) {
        try {
          const savedState = localStorage.getItem('organelleMenuCollapsed');
          if (savedState === 'true') {
            contentElement.classList.add('collapsed');
            indicatorElement.classList.add('collapsed');
          } else if (savedState === 'false') {
            contentElement.classList.remove('collapsed');
            indicatorElement.classList.remove('collapsed');
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }
    }
    
    return menuElement;
  }

  /**
   * Update resource display in the organelle menu and enable/disable buttons
   */
  public updateResources(resources: {lipids: number, glucose: number, atp?: number}): void {
    const menuElement = this.domElements.get('organelle-menu');
    if (!menuElement) return;
    
    // Update button states based on available resources
    const buttons = menuElement.querySelectorAll('.ui-button');
    buttons.forEach(button => {
      const lipidCost = parseInt(button.getAttribute('data-lipid-cost') || '0', 10);
      const glucoseCost = parseInt(button.getAttribute('data-glucose-cost') || '0', 10);
      const atpCost = parseInt(button.getAttribute('data-atp-cost') || '0', 10);
      
      // Check if player has enough resources
      const hasEnoughLipids = resources.lipids >= lipidCost;
      const hasEnoughGlucose = resources.glucose >= glucoseCost;
      const hasEnoughATP = atpCost ? (resources.atp || 0) >= atpCost : true;
      const canAfford = hasEnoughLipids && hasEnoughGlucose && hasEnoughATP;
      
      // Update button state
      if (canAfford) {
        button.classList.remove('ui-button-disabled');
      } else {
        button.classList.add('ui-button-disabled');
      }
    });
  }

  /**
   * Create a combined stats panel with health and resources
   */
  public createStatsPanel(health: number, resources: {lipids: number, glucose: number, atp?: number, maxATP?: number}, membraneStage: number = 1): HTMLElement {
    // Ensure top-left container exists
    let container = this.domElements.get('top-left-container');
    if (!container) {
      container = this.createTopLeftContainer();
    }

    // Get capacity based on membrane stage
    const capacity = getResourceCapacityForStage(membraneStage);

    // Define max values - use provided maxATP if available
    const maxHealth = 100;
    const maxATP = resources.maxATP !== undefined ? resources.maxATP : capacity.maxATP;
    const maxLipids = capacity.maxLipids;
    const maxGlucose = capacity.maxGlucose;

    // Calculate percentage filled for progress bars
    const healthPercentage = Math.min(100, (health / maxHealth) * 100);
    const atpPercentage = Math.min(100, ((resources.atp || 0) / maxATP) * 100);
    const lipidPercentage = Math.min(100, (resources.lipids / maxLipids) * 100);
    const glucosePercentage = Math.min(100, (resources.glucose / maxGlucose) * 100);

    // Create HTML for stats panel
    const statsHTML = `
      <div class="ui-container ui-panel stats-panel" style="pointer-events: auto;">
        <div class="stats-container">
          <div class="stat-row">
            <div class="stat-bar-container">
              <div class="stat-bar">
                <div class="stat-bar-fill health-bar-fill" style="width: ${healthPercentage}%;"></div>
              </div>
              <div class="stat-value">Health: ${health}/${maxHealth}</div>
            </div>
          </div>
          <div class="stat-row">
            <div class="stat-bar-container">
              <div class="stat-bar" style="position: relative;">
                <div class="stat-bar-fill atp-bar-fill" style="width: ${atpPercentage}%; background-color: #FFDD00;"></div>
              </div>
              <div class="stat-value">ATP: ${resources.atp || 0}/${maxATP}</div>
            </div>
          </div>
          <div class="stat-row">
            <div class="stat-bar-container">
              <div class="stat-bar" style="position: relative;">
                <div class="stat-bar-fill lipid-bar-fill" style="width: ${lipidPercentage}%;"></div>
              </div>
              <div class="stat-value">Lipids: ${resources.lipids}/${maxLipids}</div>
            </div>
          </div>
          <div class="stat-row">
            <div class="stat-bar-container">
              <div class="stat-bar" style="position: relative;">
                <div class="stat-bar-fill glucose-bar-fill" style="width: ${glucosePercentage}%;"></div>
              </div>
              <div class="stat-value">Glucose: ${resources.glucose}/${maxGlucose}</div>
            </div>
          </div>
          <div class="membrane-stage-indicator">Membrane Stage: ${membraneStage}</div>
        </div>
      </div>
    `;
    
    // Create the element directly in the DOM
    const statsElement = this.createDOMElement('stats-panel-dom', statsHTML);
    
    // Move it into the top-left container
    const containerInner = container.querySelector('#top-left-container-inner');
    if (containerInner) {
      containerInner.appendChild(statsElement);
    }
    
    return statsElement;
  }

  /**
   * Update the stats panel with new health and resource values
   */
  public updateStatsPanel(health: number, resources: {lipids: number, glucose: number, atp?: number, maxATP?: number}, membraneStage: number = 1): void {
    // Get the stats panel element
    const statsPanel = this.domElements.get('stats-panel-dom');
    if (!statsPanel) return;
    
    // Get capacity based on membrane stage
    const capacity = getResourceCapacityForStage(membraneStage);

    // Define max values - use provided maxATP if available, otherwise use from capacity
    const maxHealth = 100;
    const maxATP = resources.maxATP !== undefined ? resources.maxATP : capacity.maxATP;
    const maxLipids = capacity.maxLipids;
    const maxGlucose = capacity.maxGlucose;

    // Calculate percentage filled for progress bars
    const healthPercentage = Math.min(100, (health / maxHealth) * 100);
    const atpPercentage = Math.min(100, ((resources.atp || 0) / maxATP) * 100);
    const lipidPercentage = Math.min(100, (resources.lipids / maxLipids) * 100);
    const glucosePercentage = Math.min(100, (resources.glucose / maxGlucose) * 100);

    // Update each bar's width
    const healthBar = statsPanel.querySelector('.health-bar-fill') as HTMLElement;
    if (healthBar) healthBar.style.width = `${healthPercentage}%`;
    
    const atpBar = statsPanel.querySelector('.atp-bar-fill') as HTMLElement;
    if (atpBar) {
      atpBar.style.width = `${atpPercentage}%`;
      // Remove old classes that might still be applied
      atpBar.classList.remove('full-bar', 'high-bar', 'near-capacity-pulse', 'high-capacity');
      
      // Update the stat bar container - remove any existing capacity indicators
      const atpBarContainer = atpBar.closest('.stat-bar');
      if (atpBarContainer) {
        // Remove all capacity indicators
        const existingAtpBorders = atpBarContainer.querySelectorAll('.capacity-bar-border');
        existingAtpBorders.forEach(el => el.remove());
        
        const existingAtpPatterns = atpBarContainer.querySelectorAll('.capacity-pattern');
        existingAtpPatterns.forEach(el => el.remove());
      }
    }
    
    const lipidBar = statsPanel.querySelector('.lipid-bar-fill') as HTMLElement;
    if (lipidBar) {
      lipidBar.style.width = `${lipidPercentage}%`;
      // Remove old classes that might still be applied
      lipidBar.classList.remove('full-bar', 'high-bar', 'near-capacity-pulse', 'high-capacity');
      
      // Update the stat bar container - remove any existing capacity indicators
      const lipidBarContainer = lipidBar.closest('.stat-bar');
      if (lipidBarContainer) {
        // Remove all capacity indicators
        const existingLipidBorders = lipidBarContainer.querySelectorAll('.capacity-bar-border');
        existingLipidBorders.forEach(el => el.remove());
        
        const existingLipidPatterns = lipidBarContainer.querySelectorAll('.capacity-pattern');
        existingLipidPatterns.forEach(el => el.remove());
      }
    }
    
    const glucoseBar = statsPanel.querySelector('.glucose-bar-fill') as HTMLElement;
    if (glucoseBar) {
      glucoseBar.style.width = `${glucosePercentage}%`;
      // Remove old classes that might still be applied
      glucoseBar.classList.remove('full-bar', 'high-bar', 'near-capacity-pulse', 'high-capacity');
      
      // Update the stat bar container - remove any existing capacity indicators
      const glucoseBarContainer = glucoseBar.closest('.stat-bar');
      if (glucoseBarContainer) {
        // Remove all capacity indicators
        const existingGlucoseBorders = glucoseBarContainer.querySelectorAll('.capacity-bar-border');
        existingGlucoseBorders.forEach(el => el.remove());
        
        const existingGlucosePatterns = glucoseBarContainer.querySelectorAll('.capacity-pattern');
        existingGlucosePatterns.forEach(el => el.remove());
      }
    }
    
    // Update text values and capacity icons
    const healthText = statsPanel.querySelector('.stat-row:nth-child(1) .stat-value');
    if (healthText) healthText.textContent = `Health: ${health}/${maxHealth}`;
    
    const atpText = statsPanel.querySelector('.stat-row:nth-child(2) .stat-value');
    if (atpText) {
      // Remove existing icons
      const existingAtpIcons = atpText.querySelectorAll('.capacity-warning-icon, .capacity-high-icon');
      existingAtpIcons.forEach(el => el.remove());
      
      // Set basic text
      atpText.textContent = `ATP: ${resources.atp || 0}/${maxATP} `;
      
      // Add capacity icon if needed
      // if (atpPercentage > 90) {
      //   const icon = document.createElement('span');
      //   icon.className = 'capacity-warning-icon';
      //   atpText.appendChild(icon);
      // } else if (atpPercentage > 70) {
      //   const icon = document.createElement('span');
      //   icon.className = 'capacity-high-icon';
      //   atpText.appendChild(icon);
      // }
    }
    
    const lipidText = statsPanel.querySelector('.stat-row:nth-child(3) .stat-value');
    if (lipidText) {
      // Remove existing icons
      const existingLipidIcons = lipidText.querySelectorAll('.capacity-warning-icon, .capacity-high-icon');
      existingLipidIcons.forEach(el => el.remove());
      
      // Set basic text
      lipidText.textContent = `Lipids: ${resources.lipids}/${maxLipids} `;
      
      // Add capacity icon if needed
      // if (lipidPercentage > 90) {
      //   const icon = document.createElement('span');
      //   icon.className = 'capacity-warning-icon';
      //   lipidText.appendChild(icon);
      // } else if (lipidPercentage > 70) {
      //   const icon = document.createElement('span');
      //   icon.className = 'capacity-high-icon';
      //   lipidText.appendChild(icon);
      // }
    }
    
    const glucoseText = statsPanel.querySelector('.stat-row:nth-child(4) .stat-value');
    if (glucoseText) {
      // Remove existing icons
      const existingGlucoseIcons = glucoseText.querySelectorAll('.capacity-warning-icon, .capacity-high-icon');
      existingGlucoseIcons.forEach(el => el.remove());
      
      // Set basic text
      glucoseText.textContent = `Glucose: ${resources.glucose}/${maxGlucose} `;
      
      // Add capacity icon if needed
      if (glucosePercentage > 90) {
        const icon = document.createElement('span');
        icon.className = 'capacity-warning-icon';
        glucoseText.appendChild(icon);
      } else if (glucosePercentage > 70) {
        const icon = document.createElement('span');
        icon.className = 'capacity-high-icon';
        glucoseText.appendChild(icon);
      }
    }
    
    // Update membrane stage indicator
    const membraneStageIndicator = statsPanel.querySelector('.membrane-stage-indicator');
    if (membraneStageIndicator) membraneStageIndicator.textContent = `Membrane Stage: ${membraneStage}`;
  }

  /**
   * Clean up all elements created by this manager
   */
  public destroy(): void {
    // Clean up Phaser DOM elements
    this.elements.forEach(element => element.destroy());
    this.elements.clear();
    
    // Clean up direct DOM elements
    this.domElements.forEach(element => {
      if (element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
    this.domElements.clear();
    
    // Also clean up specific elements from both top-left and top-right containers
    const elementsToCleanup = [
      'health-bar-dom',
      'resource-counters-dom',
      'stats-panel-dom',
      'organelle-menu',
      'fullscreen-button',
      'back-button',
      'top-left-container',
      'top-right-container'
    ];
    
    elementsToCleanup.forEach(id => {
      const element = document.getElementById(id);
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
  }

  public createResourceCounters(resources: {lipids: number, glucose: number, atp?: number}): HTMLElement {
    console.warn('createResourceCounters is deprecated, use createStatsPanel instead');
    return this.createStatsPanel(100, resources);
  }

  public updateResourceCounters(resources: {lipids: number, glucose: number, atp?: number}): void {
    console.warn('updateResourceCounters is deprecated, use updateStatsPanel instead');
    const statsPanel = this.domElements.get('stats-panel-dom');
    if (!statsPanel) return;
    
    this.updateStatsPanel(
      Number(statsPanel.querySelector('.stat-row:nth-child(1) .stat-value')?.textContent?.match(/(\d+)\/\d+/)?.[1] || 100),
      resources
    );
  }

  /**
   * Handle screen resize - repositions elements as needed
   */
  public handleResize(): void {
    // No need to reposition the top-right container or top-left container as they use fixed positioning
    
    // Update notification position if it exists
    const notification = this.elements.get('notification');
    if (notification) {
      notification.x = this.scene.scale.width / 2;
    }
    
    // Adjust responsive sizing if needed based on screen width
    const width = window.innerWidth;
    const wasMobile = document.body.classList.contains('small-screen') || document.body.classList.contains('medium-screen');
    const isMobile = width <= 768;
    
    // Apply responsive classes based on screen size
    if (width <= 360) {
      // Very small screens (e.g. small phones)
      document.body.classList.add('very-small-screen');
      document.body.classList.add('small-screen');
      document.body.classList.remove('medium-screen');
    } else if (width <= 480) {
      // Small screens (e.g. phones)
      document.body.classList.add('small-screen');
      document.body.classList.remove('medium-screen');
      document.body.classList.remove('very-small-screen');
    } else if (width <= 768) {
      // Medium screens (e.g. tablets)
      document.body.classList.add('medium-screen');
      document.body.classList.remove('small-screen');
      document.body.classList.remove('very-small-screen');
    } else {
      // Large screens (e.g. desktops)
      document.body.classList.remove('small-screen');
      document.body.classList.remove('medium-screen');
      document.body.classList.remove('very-small-screen');
    }

    // Handle collapsible menu states when screen size changes
    // If going from desktop to mobile, collapse the menu
    // If going from mobile to desktop, expand the menu
    const organelleMenu = this.domElements.get('organelle-menu');
    if (organelleMenu) {
      const contentElement = organelleMenu.querySelector('.collapsible-content');
      const indicatorElement = organelleMenu.querySelector('.collapse-indicator');
      
      if (contentElement && indicatorElement) {
        if (!wasMobile && isMobile) {
          // Transitioning from desktop to mobile - collapse the menu
          contentElement.classList.add('collapsed');
          indicatorElement.classList.add('collapsed');
          
          // Store the state
          try {
            localStorage.setItem('organelleMenuCollapsed', 'true');
          } catch (e) {
            // Ignore localStorage errors
          }
        } else if (wasMobile && !isMobile) {
          // Transitioning from mobile to desktop - expand the menu
          contentElement.classList.remove('collapsed');
          indicatorElement.classList.remove('collapsed');
          
          // Store the state
          try {
            localStorage.setItem('organelleMenuCollapsed', 'false');
          } catch (e) {
            // Ignore localStorage errors
          }
        }
      }
    }

    // Adjust any specific elements that need additional adjustment
    // Access the stats panel if it exists
    const statsPanel = this.domElements.get('stats-panel-dom');
    if (statsPanel) {
      const statsPanelElement = statsPanel as HTMLElement;
      
      // You can add additional dynamic adjustments here if needed based on screen size
      // For example, you could directly adjust element styles beyond what CSS media queries can do
      
      // Ensure the stats panel is positioned correctly within its container
      if (width <= 480) {
        // Ensure the stats content is properly sized for very small screens
        const statValueElements = statsPanelElement.querySelectorAll('.stat-value');
        statValueElements.forEach(el => {
          // Ensure text doesn't overflow the container
          (el as HTMLElement).style.whiteSpace = 'nowrap';
          (el as HTMLElement).style.overflow = 'hidden';
          (el as HTMLElement).style.textOverflow = 'ellipsis';
        });
      }
    }
  }

  /**
   * Toggle fullscreen mode
   */
  private toggleFullscreen(): void {
    if (this.scene.scale.isFullscreen) {
      this.scene.scale.stopFullscreen();
    } else {
      this.scene.scale.startFullscreen();
    }
    
    // Update button icon after state change
    setTimeout(() => this.updateFullscreenButtonIcon(), 100);
  }

  /**
   * Create a membrane expansion button
   * @param stage Current membrane stage
   * @param resources Current resource counts
   * @param onClick Callback when button is clicked
   */
  public createMembraneExpansionButton(
    stage: number,
    resources: {lipids: number, glucose: number, atp?: number},
    onClick: () => void
  ): HTMLElement {
    // Ensure top-right container exists
    let container = this.domElements.get('top-right-container');
    if (!container) {
      container = this.createTopRightContainer();
    }

    // Find the next stage requirements
    let nextStage = stage + 1;
    let buttonHTML = '';
    
    if (nextStage <= 4) {
      buttonHTML = `
        <div class="ui-container ui-panel membrane-expansion" style="margin-bottom: 10px; pointer-events: auto;">
          <div class="ui-title">Membrane Expansion</div>
          <div class="membrane-expansion-content">
            <div class="membrane-stage">Current Stage: ${stage}</div>
            <button class="ui-button expand-membrane-button">
              <span>Expand to Stage ${nextStage}</span>
            </button>
          </div>
        </div>
      `;
    } else {
      buttonHTML = `
        <div class="ui-container ui-panel membrane-expansion" style="margin-bottom: 10px; pointer-events: auto;">
          <div class="ui-title">Membrane Expansion</div>
          <div class="membrane-expansion-content">
            <div class="membrane-stage">Current Stage: ${stage} (Maximum)</div>
          </div>
        </div>
      `;
    }

    // Create the element directly in the DOM
    const buttonElement = this.createDOMElement('membrane-expansion-button', buttonHTML);
    
    // Move it into the top-right container
    const containerInner = container.querySelector('#top-right-container-inner');
    if (containerInner) {
      containerInner.appendChild(buttonElement);
    }
    
    // Add event listener to button if we're not at max stage
    if (nextStage <= 4) {
      const button = buttonElement.querySelector('.expand-membrane-button');
      if (button) {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          onClick();
        });
      }
    }
    
    return buttonElement;
  }

  /**
   * Update the membrane expansion button
   */
  public updateMembraneExpansionButton(
    stage: number,
    resources: {lipids: number, glucose: number, atp?: number}
  ): void {
    // Remove existing button
    const existingButton = this.domElements.get('membrane-expansion-button');
    if (existingButton && existingButton.parentNode) {
      existingButton.parentNode.removeChild(existingButton);
    }
    
    // Re-create with updated state
    this.createMembraneExpansionButton(stage, resources, () => {
      // Re-use the existing onclick handler if possible
      const button = document.querySelector('.expand-membrane-button');
      if (button) {
        const event = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        });
        button.dispatchEvent(event);
      }
    });
  }

  /**
   * Update the organelle menu to reflect current membrane stage
   * This is called when the player's membrane stage changes
   */
  public updateMembraneInfo(membraneInfo: {
    currentStage: number,
    nextStage?: {
      stage: number,
      lipidCost: number,
      atpCost: number,
      requirements: { id: string, description: string }[]
    }
  }): void {
    // Remove the old menu and recreate it with the new membrane info
    const menuElement = this.domElements.get('organelle-menu');
    if (!menuElement) return;
    
    // Store the current collapsed state before removing the menu
    let isCollapsed = false;
    const contentElement = menuElement.querySelector('.collapsible-content');
    if (contentElement) {
      isCollapsed = contentElement.classList.contains('collapsed');
    }
    
    // Get the current data
    const organelleOptionsEls = menuElement.querySelectorAll('.ui-button[data-type]:not([data-type="expand_membrane"])');
    const organelleOptions = Array.from(organelleOptionsEls).map(el => {
      const button = el as HTMLElement;
      return {
        type: button.getAttribute('data-type') || '',
        name: button.querySelector('.organelle-name')?.textContent || '',
        lipidCost: parseInt(button.getAttribute('data-lipid-cost') || '0', 10),
        glucoseCost: parseInt(button.getAttribute('data-glucose-cost') || '0', 10),
        atpCost: button.hasAttribute('data-atp-cost') 
          ? parseInt(button.getAttribute('data-atp-cost') || '0', 10) 
          : undefined
      };
    });
    
    // Get the callback function using the dataset
    const onSelectHandler = this._callbacks.get('organelle-menu-select');
    
    // Get current resources from the menu
    const lipidEl = menuElement.querySelector('.lipid-cost');
    const glucoseEl = menuElement.querySelector('.glucose-cost');
    const atpEl = menuElement.querySelector('.atp-cost');
    
    const resources = {
      lipids: parseInt(lipidEl?.textContent || '0', 10),
      glucose: parseInt(glucoseEl?.textContent || '0', 10),
      atp: atpEl ? parseInt(atpEl.textContent || '0', 10) : 0
    };
    
    // Remove old menu
    menuElement.remove();
    
    // If we have all the data, create a new menu
    if (organelleOptions.length > 0 && onSelectHandler) {
      const newMenu = this.createOrganelleMenu(
        organelleOptions,
        resources,
        onSelectHandler,
        membraneInfo
      );
      
      // Apply the previously stored collapsed state
      if (isCollapsed) {
        try {
          const newContentElement = newMenu.querySelector('.collapsible-content');
          const newIndicatorElement = newMenu.querySelector('.collapse-indicator');
          
          if (newContentElement && newIndicatorElement) {
            newContentElement.classList.add('collapsed');
            newIndicatorElement.classList.add('collapsed');
          }
        } catch (e) {
          // Ignore errors
        }
      }
    }
  }

  /**
   * Create a generic modal dialog with custom content
   * @param id Unique ID for the modal
   * @param title Title of the modal
   * @param content HTML content to display in the modal body
   * @param options Additional options for the modal
   * @returns HTMLElement of the created modal
   */
  public createModal(
    id: string,
    title: string,
    content: string,
    options: {
      closable?: boolean;
      closeOnBackdrop?: boolean;
      onClose?: () => void;
      scrollable?: boolean;
      width?: string;
      maxHeight?: string;
    } = {}
  ): HTMLElement {
    // Set default options
    const defaultOptions = {
      closable: true,
      closeOnBackdrop: true,
      scrollable: true,
      width: '80%',
      maxHeight: '80vh'
    };

    // Merge with provided options
    const mergedOptions = { ...defaultOptions, ...options };

    // Create backdrop element
    const backdropHTML = `
      <div class="modal-backdrop"></div>
    `;
    const backdropElement = this.createDOMElement(`${id}-backdrop`, backdropHTML);

    // Create the modal HTML
    const modalHTML = `
      <div class="ui-modal" id="${id}-inner" style="width: ${mergedOptions.width}; max-width: 600px;">
        <div class="modal-header">
          <h2 class="modal-title">${title}</h2>
          ${mergedOptions.closable ? '<button class="modal-close-button">&times;</button>' : ''}
        </div>
        <div class="modal-content ${mergedOptions.scrollable ? 'scrollable' : ''}">
          ${content}
        </div>
        ${mergedOptions.closable ? 
          '<div class="modal-footer"><button class="ui-button modal-close-btn">Close</button></div>' : 
          ''}
      </div>
    `;

    // Create the modal element in the DOM
    const modalElement = this.createDOMElement(id, modalHTML);

    // Add event listeners
    if (mergedOptions.closable) {
      // Close button in header
      const closeButton = modalElement.querySelector('.modal-close-button');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          this.closeModal(id);
          if (mergedOptions.onClose) mergedOptions.onClose();
        });
      }

      // Close button in footer
      const closeButtonFooter = modalElement.querySelector('.modal-close-btn');
      if (closeButtonFooter) {
        closeButtonFooter.addEventListener('click', () => {
          this.closeModal(id);
          if (mergedOptions.onClose) mergedOptions.onClose();
        });
      }
    }

    // Close on backdrop click if enabled
    if (mergedOptions.closeOnBackdrop) {
      backdropElement.addEventListener('click', (event) => {
        if (event.target === backdropElement) {
          this.closeModal(id);
          if (mergedOptions.onClose) mergedOptions.onClose();
        }
      });
    }

    // Set the maximum height for the content area if scrollable
    if (mergedOptions.scrollable && mergedOptions.maxHeight) {
      const contentElement = modalElement.querySelector('.modal-content') as HTMLElement;
      if (contentElement) {
        contentElement.style.maxHeight = mergedOptions.maxHeight;
      }
    }

    return modalElement;
  }

  /**
   * Close and remove a modal
   * @param id The ID of the modal to close
   */
  public closeModal(id: string): void {
    // Remove the modal element
    const modalElement = this.domElements.get(id);
    if (modalElement && modalElement.parentNode) {
      modalElement.parentNode.removeChild(modalElement);
      this.domElements.delete(id);
    }

    // Remove the backdrop element
    const backdropElement = this.domElements.get(`${id}-backdrop`);
    if (backdropElement && backdropElement.parentNode) {
      backdropElement.parentNode.removeChild(backdropElement);
      this.domElements.delete(`${id}-backdrop`);
    }
  }

  /**
   * Show a welcome modal for multiplayer with game instructions
   * @param onClose Optional callback when the modal is closed
   * @returns HTMLElement of the created modal
   */
  public showWelcomeModal(onClose?: () => void): HTMLElement {
    const title = "Welcome to Cytopod!";
    
    const content = `
      <div class="welcome-modal-content">
        <p>You are a living cell in a microscopic world. Survive and thrive by collecting resources and building organelles!</p>
        
        <h3>Resources</h3>
        <p>
          • <span class="lipid-highlight">Lipids (blue)</span>: Build your cell membrane and organelles<br>
          • <span class="glucose-highlight">Glucose (yellow)</span>: Convert to energy<br>
          • <span class="atp-highlight">ATP (gold)</span>: Energy used for movement and upgrades
        </p>
        
        <h3>Organelles</h3>
        <p>
          • <strong>Mitochondria</strong>: Converts glucose into ATP energy<br>
          • <strong>Lysosomes</strong>: Produces enzymes for defense<br>
          • <strong>Expand Membrane</strong>: Increases your resource capacity
        </p>
        
        <h3>Controls</h3>
        <p>
          <strong>Desktop:</strong><br>
          • <strong>Left-click</strong> to move<br>
          • <strong>Right-click</strong> to shoot enzymes<br>
          • <strong>Scroll wheel</strong> to zoom in/out
        </p>
        <p>
          <strong>Mobile:</strong><br>
          • <strong>Tap</strong> to move<br>
          • Use the <strong>virtual joystick</strong> to shoot enzymes<br>
          • <strong>Pinch</strong> to zoom in/out
        </p>
        
        <h3>Strategy Tips</h3>
        <p>
          • Balance your resources - don't let them fill up completely<br>
          • Build mitochondria early to ensure steady energy production<br>
          • Upgrade your membrane to increase resource capacity<br>
          • Use enzymes defensively when threatened by other cells
        </p>
        
        <div class="welcome-footer">
          <p>Thanks for playing! I hope you enjoy it and I appreciate your feedback!</p>
          <a href="https://x.com/illyakonovalov" target="_blank" class="social-link">@illyakonovalov</a>
        </div>
      </div>
    `;
    
    return this.createModal("welcome-modal", title, content, {
      onClose: onClose,
      width: "90%",
      maxHeight: "70vh"
    });
  }

  /**
   * Show an info/notification message
   * @param message Message to display
   * @param duration Duration in ms to show the message (0 for permanent)
   */
  public showNotification(message: string, duration: number = 3000): void {
    const notificationHTML = `
      <div class="notification">
        ${message}
      </div>
    `;
    
    const notification = this.createElement('notification', notificationHTML, this.scene.scale.width / 2, 0);
    
    if (duration > 0) {
      setTimeout(() => {
        notification.destroy();
        this.elements.delete('notification');
      }, duration);
    }
  }

  /**
   * Create joystick containers for mobile controls
   * @returns Object containing references to both joystick containers
   */
  public createJoystickContainers(): { leftContainer: HTMLElement, rightContainer: HTMLElement } {
    // Create the left joystick container
    const leftJoystickHTML = `
      <div id="left-joystick-inner" class="joystick-container left-joystick" style="position: fixed; bottom: 100px; left: 100px; width: 100px; height: 100px; pointer-events: auto; z-index: 999;">
      </div>
    `;
    const leftJoystickElement = this.createDOMElement('left-joystick', leftJoystickHTML);
    
    // Create the right joystick container
    const rightJoystickHTML = `
      <div id="right-joystick-inner" class="joystick-container right-joystick" style="position: fixed; bottom: 100px; right: 100px; width: 100px; height: 100px; pointer-events: auto; z-index: 999;">
      </div>
    `;
    const rightJoystickElement = this.createDOMElement('right-joystick', rightJoystickHTML);
    
    // Return both containers
    return {
      leftContainer: leftJoystickElement,
      rightContainer: rightJoystickElement
    };
  }
  
  /**
   * Update the position of the joystick containers based on screen size
   */
  public updateJoystickPositions(): void {
    const leftJoystick = this.domElements.get('left-joystick');
    const rightJoystick = this.domElements.get('right-joystick');
    
    if (!leftJoystick || !rightJoystick) return;
    
    const width = window.innerWidth;
    const height = window.innerHeight;
    
    // Get inner containers
    const leftInner = leftJoystick.querySelector('#left-joystick-inner') as HTMLElement;
    const rightInner = rightJoystick.querySelector('#right-joystick-inner') as HTMLElement;
    
    if (!leftInner || !rightInner) return;
    
    // Calculate positions based on screen size
    const margin = Math.min(50, width * 0.05);
    const size = Math.min(120, Math.max(80, width * 0.15));
    const bottom = Math.min(120, height * 0.15);
    
    // Update left joystick
    leftInner.style.left = `${margin}px`;
    leftInner.style.bottom = `${bottom}px`;
    leftInner.style.width = `${size}px`;
    leftInner.style.height = `${size}px`;
    
    // Update right joystick
    rightInner.style.right = `${margin}px`;
    rightInner.style.bottom = `${bottom}px`;
    rightInner.style.width = `${size}px`;
    rightInner.style.height = `${size}px`;
  }
  
  /**
   * Show or hide joysticks based on device type and window size
   * @param forceVisible Force joysticks to be visible (for testing)
   */
  public toggleJoystickVisibility(forceVisible: boolean = false): void {
    const leftJoystick = this.domElements.get('left-joystick');
    const rightJoystick = this.domElements.get('right-joystick');
    
    if (!leftJoystick || !rightJoystick) return;
    
    // Check if this is a mobile device or small viewport
    const isMobile = window.innerWidth <= 768 || forceVisible;
    
    // Only show joysticks on mobile or when forced
    if (isMobile) {
      leftJoystick.style.display = 'block';
      rightJoystick.style.display = 'block';
      // Update positions when showing
      this.updateJoystickPositions();
    } else {
      leftJoystick.style.display = 'none';
      rightJoystick.style.display = 'none';
    }
  }
} 